import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import SEO from '../../components/SEO';
import { 
  ArrowRight, CheckCircle, TrendingDown, Shield, Target, Bell, 
  ChevronRight, Activity, AlertTriangle, BarChart3
} from 'lucide-react';

const faqData = [
  {
    question: "SHORT sinyal nedir?",
    answer: "SHORT sinyal, fiyatın düşeceği beklentisiyle açılan pozisyon bildirimidir. Borsamex'in Sniper Engine'i aşağı yönlü teknik yapıları tespit ederek SHORT fırsatları belirler."
  },
  {
    question: "SHORT sinyaller nasıl çalışır?",
    answer: "Sistem aşağı yönlü trend yapılarını, direnç bölgelerini ve momentum zayıflamalarını analiz eder. Uygun koşullar oluştuğunda giriş, TP ve SL seviyeleri ile SHORT sinyal yayınlar."
  },
  {
    question: "SHORT pozisyon riski nedir?",
    answer: "SHORT pozisyonlar teorik olarak sınırsız zarar potansiyeli taşır (fiyat sonsuza kadar yükselebilir). Bu nedenle stop loss kullanımı kritik öneme sahiptir. Borsamex her sinyalde SL seviyesi belirtir."
  },
  {
    question: "Hangi kripto paritelerde SHORT sinyal var?",
    answer: "BTC, ETH, SOL ve diğer majör paritelerde SHORT sinyaller üretilir. Sistem piyasa koşullarına göre her iki yönde (LONG/SHORT) fırsat arar."
  }
];

const structuredData = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqData.map(item => ({
    "@type": "Question",
    "name": item.question,
    "acceptedAnswer": { "@type": "Answer", "text": item.answer }
  }))
};

const ShortSinyallerPage = () => {
  return (
    <div className="min-h-screen bg-brand-dark">
      <SEO 
        title="SHORT Sinyaller – Düşüş Yönlü Trading Fırsatları | Borsamex"
        description="SHORT sinyaller ile düşen piyasalardan faydalanın. Sniper Engine aşağı yönlü teknik yapıları analiz eder, TP/SL seviyeleri ile risk odaklı SHORT sinyaller üretir."
        keywords="short sinyaller, short kripto sinyalleri, düşüş sinyalleri, bearish sinyaller, kripto short, aşağı yönlü sinyal"
        canonical="https://borsamex.com/short-sinyaller"
        structuredData={structuredData}
      />

      {/* HERO */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-red-500/5 via-transparent to-brand-green/5" />
        <div className="container mx-auto px-4 max-w-5xl relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
            <Badge className="mb-6 px-4 py-2 bg-red-500/10 text-red-400 border-red-500/20">
              <TrendingDown className="h-4 w-4 mr-2" />
              Düşüş Yönlü Fırsatlar
            </Badge>
            
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-white mb-6 leading-tight">
              SHORT Sinyaller<br />
              <span className="text-red-400">Düşen Piyasalardan Kazanç</span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-3xl mx-auto">
              Borsamex'in <strong className="text-white">Sniper Engine</strong> sistemi, 
              aşağı yönlü teknik yapıları tespit ederek <strong className="text-red-400">SHORT</strong> fırsatları belirler. 
              Piyasa düşerken de potansiyel fırsatları kaçırma.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button size="lg" className="bg-red-500 hover:bg-red-600 text-white font-bold px-8">
                  SHORT Sinyalleri Gör <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/long-sinyaller">
                <Button size="lg" variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800">
                  LONG Sinyaller
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SHORT Nedir */}
      <section className="py-16 bg-slate-900/50">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              SHORT Sinyal Sistemi Nasıl Çalışır?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <Card className="border-red-500/30 bg-gradient-to-br from-red-900/20 to-slate-900/80">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
                      <TrendingDown className="h-6 w-6 text-red-400" />
                    </div>
                    <div>
                      <span className="font-bold text-white text-xl">SHORT POZİSYON</span>
                      <p className="text-sm text-slate-400">Düşüş beklentisi</p>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="p-4 bg-slate-800/50 rounded-xl">
                      <p className="text-xs text-slate-500 mb-1">GİRİŞ (Satış)</p>
                      <p className="font-mono text-xl text-white">$97,500</p>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-green-900/20 rounded-lg border border-green-500/20">
                      <span className="text-xs text-green-400">TP1 (Kar)</span>
                      <span className="font-mono text-green-400">$95,000</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-emerald-900/20 rounded-lg border border-emerald-500/20">
                      <span className="text-xs text-emerald-400">TP2 (Kar)</span>
                      <span className="font-mono text-emerald-400">$92,000</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-red-900/20 rounded-lg border border-red-500/20">
                      <span className="text-xs text-red-400">STOP LOSS</span>
                      <span className="font-mono text-red-400">$99,500</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                  <Activity className="h-5 w-5 text-red-400" />
                  Düşüş Yapıları Tespiti
                </h3>
                <p className="text-slate-400">
                  Direnç bölgeleri, düşüş trendleri ve momentum zayıflamaları analiz edilir. 
                  Aşağı yönlü baskının güçlü olduğu noktalar tespit edilir.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                  <Target className="h-5 w-5 text-brand-green" />
                  Ters TP Mantığı
                </h3>
                <p className="text-slate-400">
                  SHORT'ta TP seviyeleri giriş fiyatının ALTINDA belirlenir. Fiyat düştükçe kâr artar. 
                  3 kademeli kar alma ile pozisyon yönetimi yapılır.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                  <Shield className="h-5 w-5 text-amber-400" />
                  Stop Loss Kritik
                </h3>
                <p className="text-slate-400">
                  SHORT pozisyonlarda SL giriş fiyatının ÜSTÜNDE belirlenir. Fiyat beklenmedik şekilde 
                  yükselirse zarar sınırlanır. SL kullanımı zorunludur.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Risk Uyarısı */}
      <section className="py-12">
        <div className="container mx-auto px-4 max-w-3xl">
          <Card className="border-amber-500/30 bg-amber-950/20">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <AlertTriangle className="h-8 w-8 text-amber-400 shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-bold text-amber-400 mb-2">SHORT Pozisyon Risk Uyarısı</h3>
                  <p className="text-slate-300 text-sm leading-relaxed">
                    SHORT pozisyonlar teorik olarak sınırsız zarar potansiyeli taşır (fiyat sonsuza kadar yükselebilir). 
                    Bu nedenle <strong className="text-white">stop loss kullanımı kritik öneme sahiptir</strong>. 
                    Sadece kaybetmeyi göze alabileceğiniz miktarla işlem yapın.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* İlgili Sayfalar - Internal Linking */}
      <section className="py-16 bg-slate-900/50">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">İlgili Sinyal Türleri</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <Link to="/long-sinyaller" className="group">
              <Card className="border-green-500/20 bg-slate-900/50 h-full hover:border-green-500/50 transition-colors">
                <CardContent className="p-6">
                  <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center mb-3">
                    <TrendingDown className="h-5 w-5 text-green-400 rotate-180" />
                  </div>
                  <h3 className="font-bold text-white mb-2 group-hover:text-green-400 transition-colors">LONG Sinyaller</h3>
                  <p className="text-slate-400 text-sm">Yükseliş yönlü fırsatları keşfet</p>
                </CardContent>
              </Card>
            </Link>

            <Link to="/btc-sinyalleri" className="group">
              <Card className="border-amber-500/20 bg-slate-900/50 h-full hover:border-amber-500/50 transition-colors">
                <CardContent className="p-6">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center mb-3">
                    <span className="text-lg">₿</span>
                  </div>
                  <h3 className="font-bold text-white mb-2 group-hover:text-amber-400 transition-colors">BTC Sinyalleri</h3>
                  <p className="text-slate-400 text-sm">Bitcoin özelinde tüm sinyaller</p>
                </CardContent>
              </Card>
            </Link>

            <Link to="/ai-kripto-sinyalleri" className="group">
              <Card className="border-purple-500/20 bg-slate-900/50 h-full hover:border-purple-500/50 transition-colors">
                <CardContent className="p-6">
                  <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center mb-3">
                    <Activity className="h-5 w-5 text-purple-400" />
                  </div>
                  <h3 className="font-bold text-white mb-2 group-hover:text-purple-400 transition-colors">AI Kripto Sinyalleri</h3>
                  <p className="text-slate-400 text-sm">Tüm paritelerde AI destekli sistem</p>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </section>

      {/* Özellikler */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: <TrendingDown className="h-7 w-7" />, title: "Düşüş Tespiti", desc: "Bearish yapı analizi", color: "bg-red-500/20 text-red-400" },
              { icon: <Target className="h-7 w-7" />, title: "3 TP Seviyesi", desc: "Kademeli kar alma", color: "bg-green-500/20 text-green-400" },
              { icon: <Shield className="h-7 w-7" />, title: "ATR Bazlı SL", desc: "Risk kontrolü", color: "bg-amber-500/20 text-amber-400" },
              { icon: <Bell className="h-7 w-7" />, title: "Anlık Bildirim", desc: "Hızlı aksiyon", color: "bg-purple-500/20 text-purple-400" }
            ].map((item, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.1 }} viewport={{ once: true }}>
                <Card className="border-slate-700/50 bg-slate-900/50 h-full text-center">
                  <CardContent className="p-6">
                    <div className={`w-14 h-14 rounded-2xl ${item.color} flex items-center justify-center mx-auto mb-4`}>{item.icon}</div>
                    <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                    <p className="text-slate-400 text-sm">{item.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Rehber Bölümü */}
      <section className="py-16 bg-slate-900/50">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">Temel Kavramları Öğren</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <Link to="/rehber/long-ve-short-ne-demek" className="group">
              <Card className="border-blue-500/20 bg-slate-900/50 h-full hover:border-blue-500/50 transition-colors">
                <CardContent className="p-5">
                  <h3 className="font-bold text-white mb-2 group-hover:text-blue-400 transition-colors">LONG ve SHORT Nedir?</h3>
                  <p className="text-slate-400 text-sm">Yön kavramlarını detaylı öğren</p>
                </CardContent>
              </Card>
            </Link>
            
            <Link to="/rehber/tp-ve-stop-loss-nedir" className="group">
              <Card className="border-green-500/20 bg-slate-900/50 h-full hover:border-green-500/50 transition-colors">
                <CardContent className="p-5">
                  <h3 className="font-bold text-white mb-2 group-hover:text-green-400 transition-colors">TP ve Stop Loss Nedir?</h3>
                  <p className="text-slate-400 text-sm">Hedef ve risk seviyeleri</p>
                </CardContent>
              </Card>
            </Link>
            
            <Link to="/rehber/yeni-baslayanlar-icin-risk-yonetimi" className="group">
              <Card className="border-red-500/20 bg-slate-900/50 h-full hover:border-red-500/50 transition-colors">
                <CardContent className="p-5">
                  <h3 className="font-bold text-white mb-2 group-hover:text-red-400 transition-colors">Risk Yönetimi</h3>
                  <p className="text-slate-400 text-sm">Sermaye koruma stratejileri</p>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">SHORT Sinyaller Hakkında</h2>
          </div>

          <div className="space-y-4">
            {faqData.map((item, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }} viewport={{ once: true }}>
                <details className="group">
                  <summary className="flex items-center justify-between p-4 bg-slate-800/50 border border-slate-700 rounded-lg cursor-pointer hover:bg-slate-800 transition-colors">
                    <span className="font-medium text-white pr-4">{item.question}</span>
                    <ChevronRight className="h-5 w-5 text-slate-400 group-open:rotate-90 transition-transform shrink-0" />
                  </summary>
                  <div className="px-4 py-3 text-sm text-slate-400 leading-relaxed">{item.answer}</div>
                </details>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-t from-red-500/10 to-transparent">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">SHORT Sinyalleri Takip Et</h2>
            <p className="text-lg text-slate-400 mb-8">Düşen piyasalarda da fırsat kaçırma.</p>
            
            <Link to="/register">
              <Button size="lg" className="bg-red-500 hover:bg-red-600 text-white font-bold px-10 py-6 text-lg rounded-xl">
                Ücretsiz Başla <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            
            <p className="mt-8 text-xs text-slate-600 max-w-xl mx-auto">
              SHORT pozisyonlar yüksek risk taşır. Borsamex sistem çıktıları yatırım tavsiyesi niteliği taşımaz.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ShortSinyallerPage;
